/** Storage nearly full: at least this share used, or less than `LOW_REMAINING` left. */
const LOW_SHARE = 0.8;
const LOW_REMAINING = 50 * 1024 * 1024;

export interface StorageHealth {
  usage: number | null;
  quota: number | null;
  /** Whether the browser promised not to evict this origin's data; null where it can't say. */
  persisted: boolean | null;
  /** Whole percent used, null when unknown. */
  percent: number | null;
  low: boolean;
}

type StorageLike = Pick<StorageManager, "estimate"> & Partial<Pick<StorageManager, "persist" | "persisted">>;

export function assessStorage(input: Pick<StorageHealth, "usage" | "quota" | "persisted">): StorageHealth {
  const { usage, quota, persisted } = input;
  if (usage === null || quota === null || quota <= 0)
    return { usage, quota, persisted, percent: null, low: false };
  return {
    usage,
    quota,
    persisted,
    percent: Math.round((usage / quota) * 100),
    low: usage / quota >= LOW_SHARE || quota - usage < LOW_REMAINING,
  };
}

/**
 * The device's storage for the offline outbox (PRD §21): how full it is, and whether it is persistent.
 * With `requestPersistence`, asks once for persistent storage where the browser supports it, so queued
 * sales aren't evicted under storage pressure. Local storage is still not a server backup.
 */
export async function readStorageHealth(
  storage: StorageLike | undefined = globalThis.navigator?.storage,
  options: { requestPersistence?: boolean } = {},
): Promise<StorageHealth> {
  if (!storage?.estimate) return assessStorage({ usage: null, quota: null, persisted: null });
  let persisted: boolean | null = null;
  try {
    persisted = storage.persisted ? await storage.persisted() : null;
    if (persisted === false && options.requestPersistence && storage.persist)
      persisted = await storage.persist();
  } catch {
    persisted = null;
  }
  try {
    const estimate = await storage.estimate();
    return assessStorage({ usage: estimate.usage ?? null, quota: estimate.quota ?? null, persisted });
  } catch {
    return assessStorage({ usage: null, quota: null, persisted });
  }
}
